import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
  Chip,
  Paper,
  Divider,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import PsychologyIcon from "@mui/icons-material/Psychology";
import DevicesIcon from "@mui/icons-material/Devices";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import SecurityIcon from "@mui/icons-material/Security";

const PricingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get plan and billing from URL params
  const urlPlan = searchParams.get("plan") || "premium";
  const urlBilling = searchParams.get("billing") || "annual";

  const [billingCycle, setBillingCycle] = useState(urlBilling);
  const [selectedPlan, setSelectedPlan] = useState(urlPlan);

  const pricingPlans = {
    monthly: {
      premium: 19,
      provider: 99,
      enterprise: 499,
    },
    annual: {
      premium: 199,
      provider: 999,
      enterprise: 4999,
    },
  };

  const savingsPercentage = {
    premium: Math.round(((12 * 19 - 199) / (12 * 19)) * 100),
    provider: Math.round(((12 * 99 - 999) / (12 * 99)) * 100),
    enterprise: Math.round(((12 * 499 - 4999) / (12 * 499)) * 100),
  };

  const features = {
    premium: [
      {
        text: "Full 200+ glossary terms",
        icon: <VolumeUpIcon />,
        highlight: true,
      },
      {
        text: "Professional audio recordings",
        icon: <StarIcon />,
        highlight: true,
      },
      { text: "Unlimited bookmarks (synced)", icon: <BookmarkIcon /> },
      { text: "Download audio files (MP3)", icon: <CloudDownloadIcon /> },
      {
        text: "AI personalized glossary",
        icon: <PsychologyIcon />,
        highlight: true,
      },
      { text: "Offline access", icon: <DevicesIcon /> },
      { text: "Advanced clinical calculators", icon: <CheckCircleIcon /> },
      { text: "Daily medication alerts", icon: <CheckCircleIcon /> },
      { text: "Trial cost comparison", icon: <CheckCircleIcon /> },
      { text: "Document manager", icon: <CheckCircleIcon /> },
      { text: "Lab & medication tracker", icon: <CheckCircleIcon /> },
      { text: "Trial calendar", icon: <CheckCircleIcon /> },
      { text: "No ads", icon: <CheckCircleIcon /> },
      { text: "Priority email support", icon: <SupportAgentIcon /> },
    ],
    provider: [
      { text: "All Premium features", icon: <CheckCircleIcon />, bold: true },
      { text: "Manage multiple patients", icon: <CheckCircleIcon /> },
      { text: "Batch trial matching", icon: <CheckCircleIcon /> },
      { text: "Patient analytics dashboard", icon: <CheckCircleIcon /> },
      { text: "White-label reports", icon: <CheckCircleIcon /> },
      { text: "Clinical calculators (Mayo, dFLC)", icon: <CheckCircleIcon /> },
      { text: "Referral tracking", icon: <CheckCircleIcon /> },
      { text: "API access", icon: <CheckCircleIcon /> },
      { text: "Custom EMR integrations", icon: <CheckCircleIcon /> },
      { text: "Priority phone support", icon: <SupportAgentIcon /> },
      { text: "Training sessions", icon: <CheckCircleIcon /> },
    ],
    enterprise: [
      { text: "All Provider features", icon: <CheckCircleIcon />, bold: true },
      { text: "Multi-user accounts (unlimited)", icon: <CheckCircleIcon /> },
      { text: "Custom EMR integrations", icon: <CheckCircleIcon /> },
      { text: "On-premise deployment option", icon: <CheckCircleIcon /> },
      { text: "Dedicated training sessions", icon: <CheckCircleIcon /> },
      {
        text: "Priority feature requests",
        icon: <StarIcon />,
        highlight: true,
      },
      { text: "Usage analytics & reporting", icon: <CheckCircleIcon /> },
      { text: "SLA guarantee (99.9% uptime)", icon: <SecurityIcon /> },
      {
        text: "Dedicated account manager",
        icon: <SupportAgentIcon />,
        highlight: true,
      },
      { text: "24/7 phone support", icon: <SupportAgentIcon /> },
    ],
  };

  const handleCheckout = (plan) => {
    console.log(`Proceeding to checkout: ${plan} - ${billingCycle}`);
    // TODO: Integrate with Stripe checkout
    // For now, navigate to a checkout page
    navigate(`/checkout?plan=${plan}&billing=${billingCycle}`);
  };

  const plans = [
    {
      id: "premium",
      name: "Premium",
      subtitle: "For Patients & Caregivers",
      color: "primary",
      popular: true,
    },
    {
      id: "provider",
      name: "Provider",
      subtitle: "For Healthcare Professionals",
      color: "secondary",
      popular: false,
    },
    {
      id: "enterprise",
      name: "Enterprise",
      subtitle: "For Healthcare Organizations",
      color: "warning",
      popular: false,
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Typography
          variant="h2"
          fontWeight="bold"
          gutterBottom
          sx={{
            background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Choose Your Plan
        </Typography>
        <Typography variant="h5" color="text.secondary" sx={{ mb: 3 }}>
          Get the most out of NephroTrax
        </Typography>

        {/* Billing Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={billingCycle === "annual"}
              onChange={(e) =>
                setBillingCycle(e.target.checked ? "annual" : "monthly")
              }
              color="primary"
            />
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body1"
                fontWeight={billingCycle === "monthly" ? "bold" : "normal"}
              >
                Monthly
              </Typography>
              <Typography variant="body2" color="text.secondary">
                |
              </Typography>
              <Typography
                variant="body1"
                fontWeight={billingCycle === "annual" ? "bold" : "normal"}
              >
                Annual
              </Typography>
              {billingCycle === "annual" && (
                <Chip
                  label="Save up to 17%"
                  size="small"
                  color="success"
                  sx={{ fontWeight: "bold", ml: 1 }}
                />
              )}
            </Box>
          }
        />
      </Box>

      {/* Pricing Cards */}
      <Grid container spacing={4} justifyContent="center">
        {plans.map((plan) => (
          <Grid item xs={12} md={4} key={plan.id}>
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                border: plan.popular ? "3px solid" : "2px solid",
                borderColor: plan.popular ? `${plan.color}.main` : "grey.300",
                position: "relative",
                transform: plan.popular ? "scale(1.05)" : "scale(1)",
                boxShadow: plan.popular ? 6 : 2,
                transition: "all 0.3s",
                "&:hover": {
                  transform: plan.popular ? "scale(1.07)" : "scale(1.02)",
                  boxShadow: 8,
                },
              }}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -15,
                    left: "50%",
                    transform: "translateX(-50%)",
                    bgcolor: `${plan.color}.main`,
                    color: "white",
                    px: 3,
                    py: 0.5,
                    borderRadius: 2,
                    fontWeight: "bold",
                    fontSize: "0.85rem",
                    boxShadow: 2,
                  }}
                >
                  ⭐ MOST POPULAR
                </Box>
              )}

              <CardContent sx={{ flexGrow: 1, p: 4, pt: plan.popular ? 5 : 4 }}>
                {/* Plan Name */}
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  gutterBottom
                  color={`${plan.color}.main`}
                >
                  {plan.name}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {plan.subtitle}
                </Typography>

                {/* Price */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    color={`${plan.color}.main`}
                  >
                    ${pricingPlans[billingCycle][plan.id]}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {billingCycle === "monthly" ? "per month" : "per year"}
                  </Typography>
                  {billingCycle === "annual" && (
                    <Typography
                      variant="caption"
                      color="success.main"
                      fontWeight="bold"
                      display="block"
                    >
                      Save $
                      {12 * pricingPlans.monthly[plan.id] -
                        pricingPlans.annual[plan.id]}
                      ({savingsPercentage[plan.id]}% off)
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Features */}
                <List dense>
                  {features[plan.id].map((feature, idx) => (
                    <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon
                        sx={{
                          minWidth: 36,
                          color: feature.highlight
                            ? `${plan.color}.main`
                            : "success.main",
                        }}
                      >
                        {feature.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={feature.text}
                        primaryTypographyProps={{
                          fontSize: "0.9rem",
                          fontWeight:
                            feature.bold || feature.highlight ? 600 : 400,
                          color: feature.highlight
                            ? `${plan.color}.main`
                            : "inherit",
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>

              {/* CTA Button */}
              <Box sx={{ p: 3, pt: 0 }}>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  color={plan.color}
                  onClick={() => handleCheckout(plan.id)}
                  sx={{
                    fontWeight: "bold",
                    py: 1.5,
                    background: plan.popular
                      ? "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)"
                      : undefined,
                  }}
                >
                  {plan.id === "enterprise" ? "Contact Sales" : "Get Started"}
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Trust Signals */}
      <Paper elevation={2} sx={{ mt: 6, p: 4, bgcolor: "grey.50" }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4} sx={{ textAlign: "center" }}>
            <SecurityIcon sx={{ fontSize: 40, color: "success.main", mb: 1 }} />
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              30-Day Money-Back
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Not satisfied? Get a full refund within 30 days.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: "center" }}>
            <CheckCircleIcon
              sx={{ fontSize: 40, color: "primary.main", mb: 1 }}
            />
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Cancel Anytime
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No long-term contracts. Cancel with one click.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: "center" }}>
            <SupportAgentIcon
              sx={{ fontSize: 40, color: "secondary.main", mb: 1 }}
            />
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Expert Support
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Get help from our dedicated support team.
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* FAQ Section */}
      <Box sx={{ mt: 6, textAlign: "center" }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Frequently Asked Questions
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Have questions? We're here to help.
        </Typography>
        <Button
          variant="outlined"
          size="large"
          onClick={() => navigate("/contact")}
        >
          Contact Support
        </Button>
      </Box>
    </Container>
  );
};

export default PricingPage;
